import { Injectable, inject } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    auditTime,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    of,
    tap
} from 'rxjs';

import { LanguageService } from '../language/language.service';
import { Language } from '../language/language.types';
import { Logger } from '../logging/logger.service';

import { RWC_TRANSLATION_CONFIG } from './translation.provider';
import { NgxTranslationData, TranslationData, TranslationParameter } from './translation.types';

/* eslint-disable @angular-eslint/prefer-inject */

@Injectable({ providedIn: 'root' })
export class MissingKeys {
    #translationConfig = inject(RWC_TRANSLATION_CONFIG, { optional: true });
    #dataSubject = new BehaviorSubject<Set<string>>(new Set());
    #loggerStarted = false;
    #logger$ = this.#dataSubject.pipe(
        auditTime(500),
        filter(set => set.size > 0),
        map(set => Array.from(set)),
        tap(keys => {
            if (!this.#translationConfig?.suppressWarnings) {
                this.logger.warn(`missing translation for keys: ${keys}`);
            }
        }),
        tap(() => this.#dataSubject.getValue().clear())
    );

    constructor(private logger: Logger) {
        this.logger = logger.createCustomLogger('TranslationService');
    }

    add(key: string): void {
        if (!this.#loggerStarted) {
            this.#loggerStarted = true;
            this.#logger$.subscribe();
        }
        this.#dataSubject.next(this.#dataSubject.getValue().add(key));
    }

    delete(key: string): void {
        this.#dataSubject.getValue().delete(key);
    }
}

/**
 * format:  { language: { translationKey: 'text' } }
 */
type LanguageTranslation = Partial<Record<Language, NgxTranslationData | { default: NgxTranslationData }>>;
/**
 * This service is only for internal use. Do *NOT* import it in your project, there is no API warranty.
 */
@Injectable({ providedIn: 'root' })
export class InternalTranslationService {
    #dataSubject = new BehaviorSubject<TranslationData>({});
    #languageService = inject(LanguageService);
    #logger = inject(Logger);
    #missingKeys = inject(MissingKeys);

    constructor() {
        this.#logger = this.#logger.createCustomLogger('TranslationService');
    }

    use(lang: Language): void {
        this.#languageService.use(lang);
    }

    getLanguage(): Observable<Language> {
        return this.#languageService.language$;
    }

    addTranslation(rawData: TranslationData | { default: TranslationData }): void {
        // when json are imported using `* as data` syntax, there is a 'default' key.
        const data = (rawData.default ?? rawData) as TranslationData;
        if (Object.keys(data).length === 0) {
            this.#logger.error('No translation data provided, you must at least provide one key value object.');
            return;
        }

        if (!isTranslationData(data)) {
            this.#logger.error(
                `Error loading translations. The format of the translation data is wrong. Translation data received: ${JSON.stringify(
                    data
                )}. Expected format: { key: { language: 'value' } }`
            );
        }
        this.#addTranslations(data);
    }

    /**
     * @param rawData translation data, optional which of the languages are provided
     */
    setTranslations(rawData: LanguageTranslation): void {
        if (Object.keys(rawData).length === 0) {
            this.#logger.error('No translation data provided, you must provide one language at least.');
            return;
        }

        // when json are imported using `* as data` syntax, there is a 'default' key.
        const data: LanguageTranslation = Object.fromEntries(
            Object.entries(rawData).map(([k, v]) => [k, v.default ?? v])
        );
        if (!isLanguageTranslation(data)) {
            this.#logger.error(
                `Error loading translations. The format of the translation data is wrong. Translation data received: ${JSON.stringify(
                    rawData
                )}. Expected format: { language: { key: 'value' } }`
            );
        }

        const translations: TranslationData = {};
        Object.entries(data).forEach(([language, data]) => {
            Object.entries(data).forEach(([key, value]) => {
                translations[key] = { ...(translations[key] || {}), ...{ [language]: value } };
            });
        });
        this.#addTranslations(translations);
    }

    setTranslation(languageKey: Language, data: NgxTranslationData): void {
        const translations: TranslationData = {};
        Object.keys(data).forEach(key => {
            translations[key] = { [languageKey]: data[key] };
        });
        this.#addTranslations(translations);
    }

    translate(key: string, interpolateParams?: TranslationParameter): Observable<string> {
        return this.#translateToInternal(this.getLanguage(), key, interpolateParams);
    }

    translateTo(language: Language, key: string, interpolateParams?: TranslationParameter): Observable<string> {
        return this.#translateToInternal(of(language), key, interpolateParams);
    }

    #translateToInternal(
        language$: Observable<Language>,
        key: string,
        interpolateParams?: TranslationParameter
    ): Observable<string> {
        return combineLatest([language$, this.#dataSubject]).pipe(
            map(([language, translationData]) => translationData[key?.trim()]?.[language]),
            map(translated => {
                if (translated) {
                    this.#missingKeys.delete(key);
                    return this.#interpolate(translated, interpolateParams);
                } else {
                    this.#missingKeys.add(key);
                    return `??? ${key} ???`;
                }
            }),
            distinctUntilChanged()
        );
    }

    hasTranslation(key: string, lang?: Language): Observable<boolean> {
        return combineLatest([lang ? of(lang) : this.getLanguage(), this.#dataSubject]).pipe(
            map(([language, translationData]) => translationData[key.trim()]?.[language]),
            map(data => !!data),
            distinctUntilChanged()
        );
    }

    #interpolate(expr: string, params?: any): string {
        if (!params) {
            return expr;
        }
        return expr.replace(/{{\s?([^{}\s]*)\s?}}/g, (match: string, key: string) => {
            const props = key.split('.');
            const result = props.reduce((paramsValue, prop) => {
                if (paramsValue) {
                    paramsValue = paramsValue[prop];
                }
                return paramsValue;
            }, params);
            return result !== undefined ? result : match;
        });
    }

    /**
     * Merges the new TranslationData with the global one and adds language keys together.
     * If a language key already exists its value will be overwritten.
     */
    #addTranslations(data: TranslationData): void {
        const result: TranslationData = this.#dataSubject.getValue();
        Object.keys(data).forEach(translationKey => {
            const translationObject = result[translationKey];
            if (translationObject) {
                Object.keys(data[translationKey]).forEach(languageKey => {
                    translationObject[languageKey as Language] = data[translationKey][languageKey as Language];
                });
            } else {
                result[translationKey] = data[translationKey];
            }
        });
        this.#dataSubject.next(result);
    }
}

/**
 * format: { language: { translationKey: 'text' } }
 *
 * Format must be checked because it is imported from external json files and depending on the import syntax,
 * it may contain a 'default' key or have wrong types.
 */
function isLanguageTranslation(obj: Partial<Record<Language, Record<string, unknown>>>): obj is LanguageTranslation {
    return !Object.values(obj).some(
        entry =>
            typeof entry !== 'object' ||
            Object.values(entry).length === 0 ||
            Object.values(entry).some(v => v !== undefined && v !== null && typeof v !== 'string')
    );
}

/**
 * format: { translationKey: { language: 'text' } }
 *
 * Format must be checked because it is imported from external json files and depending on the import syntax,
 * it may contain a 'default' key or have wrong types.
 */
function isTranslationData(obj: Record<string, Record<string, unknown>>): obj is TranslationData {
    return !Object.values(obj).some(
        entry =>
            typeof entry !== 'object' ||
            Object.values(entry).some(v => v !== undefined && v !== null && typeof v !== 'string')
    );
}
